// Phase 2A renderer for the Combat-analysis panel.
// First sub-panel: per-ability damage breakdown.
// Future sub-panels (rage, cooldowns, debuffs, rotation deviation) will live
// here too.

import type { AnalysisResponse, AbilityBreakdown } from '@shared/analysis';
import { t } from '@shared/i18n';
import { iconNameToUrl, lookup } from './wowhead';
import { attachTooltip } from './tooltip';

// Proxied via /api/img to bypass ZAM Network's geo-block (see wowhead.ts).
const ICON_BASE = '/api/img/images/wow/icons/medium';

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => {
    switch (c) {
      case '&': return '&amp;';
      case '<': return '&lt;';
      case '>': return '&gt;';
      case '"': return '&quot;';
      case "'": return '&#39;';
      default: return c;
    }
  });
}

function fmtPct(p: number): string {
  if (!Number.isFinite(p)) return '–';
  return `${(p * 100).toFixed(1)}%`;
}

function fmtNum(n: number): string {
  if (!Number.isFinite(n)) return '–';
  return n.toLocaleString();
}

function fmtIntOrDash(n: number): string {
  return n > 0 ? String(n) : '–';
}

// Stock-style delta annotation. Used in compare mode so each numeric cell
// in slot B's row shows the diff vs the matching slot A row. The ARROW
// always reflects raw direction (▲ for higher, ▼ for lower) but the COLOUR
// reflects favourability — green when slot B is better than slot A, red
// when worse. For most stats higher is better, but glance / miss / dodge /
// parry are "lower is better", so callers pass inverted=true for those.
function deltaInt(diff: number, inverted = false): string {
  if (!Number.isFinite(diff) || diff === 0) return '';
  const arrow = diff > 0 ? '▲' : '▼';
  const isGood = inverted ? diff < 0 : diff > 0;
  const cls = isGood ? 'delta good' : 'delta bad';
  return ` <span class="${cls}">(${arrow}${Math.abs(diff).toLocaleString()})</span>`;
}
function deltaPct(diff: number, inverted = false): string {
  // diff is in absolute fraction units (e.g. 0.05 means +5pp).
  if (!Number.isFinite(diff) || Math.abs(diff) < 0.0005) return '';
  const arrow = diff > 0 ? '▲' : '▼';
  const isGood = inverted ? diff < 0 : diff > 0;
  const cls = isGood ? 'delta good' : 'delta bad';
  return ` <span class="${cls}">(${arrow}${(Math.abs(diff) * 100).toFixed(1)}%)</span>`;
}

// Pick a column-flavour class so high crit / low hit jump out visually.
function critFlavour(p: number): string {
  if (p >= 0.40) return 'good';
  if (p < 0.20) return 'bad';
  return '';
}
function hitFlavour(p: number, attempts: number): string {
  if (attempts < 5) return '';     // not enough swings to draw conclusions
  if (p >= 0.99) return 'good';
  if (p < 0.92) return 'bad';
  return '';
}

// abilityID 1 ("Word of Recall (OLD)" on wowhead) is what the combat log uses
// as the catch-all for white melee swings. We render it with a stable label
// and icon and skip the wowhead lookup, otherwise users see "Word of Recall
// (OLD)" with the placeholder icon.
const MELEE_ABILITY_ID = 1;
const MELEE_ICON_URL = `${ICON_BASE}/inv_sword_04.jpg`;

function hideOnError(img: HTMLImageElement): void {
  img.addEventListener('error', () => {
    img.style.visibility = 'hidden';
  }, { once: true });
}

function makeRow(
  a: AbilityBreakdown,
  dps: number,
  cmp?: { ability: AbilityBreakdown; dps: number },
): HTMLTableRowElement {
  const tr = document.createElement('tr');
  tr.className = 'ability-row';

  const isMelee = a.abilityId === MELEE_ABILITY_ID;
  // Per-cell deltas vs the matching slot-A row when compare mode is on.
  // Hit/crit deltas need both rows to have at least 1 attempt for the
  // percentage to be meaningful; 0/0 vs 30/100 would show ▲30% which is
  // misleading.
  const co = cmp?.ability;
  const dCasts    = cmp ? deltaInt(a.casts - co!.casts) : '';
  const dAttempts = cmp ? deltaInt(a.attempts - co!.attempts) : '';
  const dHit      = cmp && a.attempts > 0 && co!.attempts > 0
    ? deltaPct(a.hitChance - co!.hitChance) : '';
  const dCrit     = cmp && a.attempts > 0 && co!.attempts > 0
    ? deltaPct(a.critChance - co!.critChance) : '';
  // glance / miss / dodge / parry are "lower is better" — pass inverted so
  // ▲ (up) shows red and ▼ (down) shows green.
  const dGlance   = cmp ? deltaPct(a.glanceChance - co!.glanceChance, true) : '';
  const dMiss     = cmp ? deltaInt(a.outcomes.misses - co!.outcomes.misses, true) : '';
  const dDodge    = cmp ? deltaInt(a.outcomes.dodges - co!.outcomes.dodges, true) : '';
  const dParry    = cmp ? deltaInt(a.outcomes.parries - co!.outcomes.parries, true) : '';
  const dAvg      = cmp ? deltaInt(a.averageHit - co!.averageHit) : '';
  const dTotal    = cmp ? deltaInt(a.totalDamage - co!.totalDamage) : '';
  const dShare    = cmp ? deltaPct(a.share - co!.share) : '';
  const dDps      = cmp ? deltaInt(Math.round(dps) - Math.round(cmp.dps)) : '';

  // Icon + name. For melee we use a stable label/icon (see comment above).
  // Otherwise we start with whatever WCL inlined (usually null) and async-
  // upgrade via wowhead lookup.
  let iconSrc: string;
  let nameText: string;
  if (isMelee) {
    iconSrc = MELEE_ICON_URL;
    nameText = '';   // filled via t() below; we re-render every lang change
  } else {
    iconSrc = a.icon
      ? `${ICON_BASE}/${a.icon.replace(/\.(jpg|png)$/i, '')}.jpg`
      : '';
    nameText = a.name ?? `spell ${a.abilityId}`;
  }

  const o = a.outcomes;
  const wowheadHref = isMelee
    ? '#'
    : `/api/wh/classic/spell=${a.abilityId}`;
  const linkAttrs = isMelee ? '' : ' target="_blank" rel="noopener"';

  tr.innerHTML = `
    <td class="ab-name">
      <a class="ab-link"${linkAttrs} href="${wowheadHref}">
        ${iconSrc ? `<img class="icon" src="${escapeHtml(iconSrc)}" alt="" width="20" height="20" />` : ''}
        <span class="ab-name-text">${escapeHtml(nameText)}</span>
      </a>
    </td>
    <td class="num">${fmtIntOrDash(a.casts)}${dCasts}</td>
    <td class="num">${fmtIntOrDash(a.attempts)}${dAttempts}</td>
    <td class="num ${hitFlavour(a.hitChance, a.attempts)}">${a.attempts ? fmtPct(a.hitChance) : '–'}${dHit}</td>
    <td class="num ${critFlavour(a.critChance)}">${a.attempts > 0 ? fmtPct(a.critChance) : '–'}${dCrit}</td>
    <td class="num">${o.glances > 0 ? fmtPct(a.glanceChance) : '–'}${dGlance}</td>
    <td class="num muted">${fmtIntOrDash(o.misses)}${dMiss}</td>
    <td class="num muted">${fmtIntOrDash(o.dodges)}${dDodge}</td>
    <td class="num muted">${fmtIntOrDash(o.parries)}${dParry}</td>
    <td class="num">${a.averageHit > 0 ? fmtNum(a.averageHit) : '–'}${dAvg}</td>
    <td class="num strong">${fmtNum(a.totalDamage)}${dTotal}</td>
    <td class="num">${fmtPct(a.share)}${dShare}</td>
    <td class="num">${fmtNum(Math.round(dps))}${dDps}</td>
  `;

  // Hide broken icons so users don't see the browser's "image missing" glyph
  // (happens when wowhead returns icon=null for classic-only spellIDs).
  const initialImg = tr.querySelector<HTMLImageElement>('img.icon');
  if (initialImg) hideOnError(initialImg);

  if (isMelee) {
    // Localised name. Re-applied on language change since renderDynamic
    // re-runs renderAbilityBreakdown, which re-creates the row.
    const span = tr.querySelector<HTMLSpanElement>('.ab-name-text');
    if (span) span.textContent = t('ability.melee');
    // No tooltip / no wowhead lookup for melee — there isn't a meaningful
    // wowhead spell page for it, and the OLD entry's tooltip is misleading.
    return tr;
  }

  // Hover tooltip on the ability link.
  const link = tr.querySelector<HTMLAnchorElement>('a.ab-link');
  if (link) attachTooltip(link, 'spell', a.abilityId);

  // Async wowhead upgrade: localised name + icon (only if we don't have them).
  if (!a.name || !a.icon) {
    void lookup('spell', a.abilityId).then((r) => {
      if (r.name) {
        const span = tr.querySelector<HTMLSpanElement>('.ab-name-text');
        if (span) span.textContent = r.name;
      }
      if (r.icon && !a.icon) {
        const img = tr.querySelector<HTMLImageElement>('img.icon');
        if (img) {
          img.src = iconNameToUrl(r.icon);
          img.style.visibility = '';
        } else {
          // Insert an icon if we didn't have one rendered initially.
          const linkEl = tr.querySelector<HTMLAnchorElement>('a.ab-link');
          if (linkEl) {
            const img2 = document.createElement('img');
            img2.className = 'icon';
            img2.width = 20;
            img2.height = 20;
            img2.alt = '';
            img2.src = iconNameToUrl(r.icon);
            hideOnError(img2);
            linkEl.insertBefore(img2, linkEl.firstChild);
          }
        }
      }
    });
  }

  return tr;
}

// Build a placeholder breakdown for an ability slot B doesn't have but
// slot A does. Used so the rows still align position-by-position with
// slot A's table — the missing ability shows as a row of zeros (with
// red ▼ deltas reflecting the gap).
function emptyBreakdown(ref: AbilityBreakdown): AbilityBreakdown {
  return {
    abilityId: ref.abilityId,
    name: ref.name,
    icon: ref.icon,
    casts: 0,
    attempts: 0,
    landed: 0,
    outcomes: {
      hits: 0, crits: 0, glances: 0, crushes: 0,
      misses: 0, dodges: 0, parries: 0, blocks: 0,
      immunes: 0, resists: 0, absorbs: 0, unknown: 0,
    },
    totalDamage: 0,
    averageHit: 0,
    hitChance: 0,
    critChance: 0,
    glanceChance: 0,
    share: 0,
  };
}

export function renderAbilityBreakdown(
  analysis: AnalysisResponse,
  container: HTMLElement,
  opts?: { compareTo?: AnalysisResponse },
): void {
  container.replaceChildren();

  const durSec = analysis.fightDurationMs / 1000;
  const totalDps = durSec > 0 ? analysis.totalDamage / durSec : 0;
  const cmpAll = opts?.compareTo;
  const cmpDur = cmpAll ? cmpAll.fightDurationMs / 1000 : 0;
  const cmpById = cmpAll ? new Map(cmpAll.abilities.map((a) => [a.abilityId, a])) : null;

  if (analysis.abilities.length === 0) {
    const p = document.createElement('p');
    p.className = 'muted';
    p.textContent = t('panel.analysis.empty');
    container.appendChild(p);
    return;
  }

  const summary = document.createElement('p');
  summary.className = 'analysis-summary muted';
  if (analysis.aggregated) {
    // All-bosses mode: emphasise the merged-N-fights aspect over the raw
    // duration so the user knows they're not looking at a single pull.
    summary.textContent = t('analysis.summary.dpsAll', {
      n: analysis.aggregated.fightCount,
      total: fmtNum(analysis.totalDamage),
      dur: durSec.toFixed(1),
      dps: fmtNum(Math.round(totalDps)),
    });
  } else {
    summary.textContent = t('analysis.summary.dps', {
      total: fmtNum(analysis.totalDamage),
      dur: durSec.toFixed(1),
      dps: fmtNum(Math.round(totalDps)),
    });
  }
  container.appendChild(summary);

  const table = document.createElement('table');
  table.className = 'ability-table';
  table.innerHTML = `
    <thead>
      <tr>
        <th>${t('analysis.col.ability')}</th>
        <th title="${escapeHtml(t('analysis.col.castsTip'))}">${t('analysis.col.casts')}</th>
        <th title="${escapeHtml(t('analysis.col.attemptsTip'))}">${t('analysis.col.attempts')}</th>
        <th title="${escapeHtml(t('analysis.col.hitTip'))}">${t('analysis.col.hit')}</th>
        <th title="${escapeHtml(t('analysis.col.critTip'))}">${t('analysis.col.crit')}</th>
        <th title="${escapeHtml(t('analysis.col.glanceTip'))}">${t('analysis.col.glance')}</th>
        <th>${t('analysis.col.miss')}</th>
        <th>${t('analysis.col.dodge')}</th>
        <th>${t('analysis.col.parry')}</th>
        <th>${t('analysis.col.avg')}</th>
        <th>${t('analysis.col.total')}</th>
        <th>${t('analysis.col.share')}</th>
        <th>${t('analysis.col.dps')}</th>
      </tr>
    </thead>
    <tbody></tbody>
  `;
  const tbody = table.querySelector('tbody')!;

  // Build the row order. In compare mode we mirror slot A's order so each
  // row in slot B sits in the same position as the matching row in slot A
  // — the user wants 英勇 in row 1 / 近战 in row 2 in BOTH columns even if
  // slot B's totals would naturally put them differently. Abilities slot
  // B has that aren't in slot A get appended.
  let ordered: AbilityBreakdown[];
  if (cmpAll) {
    const byId = new Map(analysis.abilities.map((a) => [a.abilityId, a]));
    const matchedIds = new Set<number>();
    ordered = [];
    for (const aRef of cmpAll.abilities) {
      const a = byId.get(aRef.abilityId);
      if (a) {
        ordered.push(a);
      } else {
        ordered.push(emptyBreakdown(aRef));
      }
      matchedIds.add(aRef.abilityId);
    }
    for (const a of analysis.abilities) {
      if (!matchedIds.has(a.abilityId)) ordered.push(a);
    }
  } else {
    ordered = analysis.abilities;
  }

  for (const a of ordered) {
    const abilityDps = durSec > 0 ? a.totalDamage / durSec : 0;
    let cmp: { ability: AbilityBreakdown; dps: number } | undefined;
    if (cmpById) {
      const ca = cmpById.get(a.abilityId);
      if (ca) {
        const cd = cmpDur > 0 ? ca.totalDamage / cmpDur : 0;
        cmp = { ability: ca, dps: cd };
      }
    }
    tbody.appendChild(makeRow(a, abilityDps, cmp));
  }
  container.appendChild(table);
}
